import { useCallback, useEffect, useState } from "react";

import { useSession } from "shared/hooks/useSession";
import { sendConfirmationCode, setAccountPassword } from "services/account/rest";
import { useNotificationSnackbar } from "shared/ui";

import { CONFIRMATION_CODE_LENGTH, MIN_PASSWORD_LENGTH } from "features/Account/constants";
import { AccountState } from "features/Account/types";

export const useAccountState = (): AccountState => {
    const notify = useNotificationSnackbar();
    const { account, loading, setAccount, signOut } = useSession();

    const [password, setPassword] = useState<string>("");
    const [confirmPassword, setConfirmPassword] = useState<string>("");
    const [confirmationCode, setConfirmationCode] = useState<string>("");
    const [codeRequested, setCodeRequested] = useState<boolean>(false);
    const [codeExpiresInSeconds, setCodeExpiresInSeconds] = useState<number>(0);
    const [submitting, setSubmitting] = useState<boolean>(false);

    // Отсчёт до истечения кода: подсказывает, когда есть смысл запрашивать новый.
    useEffect(() => {
        if (!codeRequested) return;

        const timer = setInterval(
            () => setCodeExpiresInSeconds((seconds) => (seconds > 0 ? seconds - 1 : 0)),
            1000
        );

        return () => clearInterval(timer);
    }, [codeRequested]);

    const passwordError =
        password && password.length < MIN_PASSWORD_LENGTH
            ? `Пароль должен содержать минимум ${MIN_PASSWORD_LENGTH} символа`
            : "";

    const confirmPasswordError =
        confirmPassword && password !== confirmPassword ? "Пароли не совпадают" : "";

    const isPasswordValid =
        password.length >= MIN_PASSWORD_LENGTH && password === confirmPassword;

    const isConfirmationCodeValid = confirmationCode.length === CONFIRMATION_CODE_LENGTH;

    const resetForm = useCallback(() => {
        setPassword("");
        setConfirmPassword("");
        setConfirmationCode("");
        setCodeRequested(false);
        setCodeExpiresInSeconds(0);
    }, []);

    const requestCode = useCallback(async () => {
        if (!isPasswordValid) return;

        setSubmitting(true);
        try {
            const result = await sendConfirmationCode();

            setCodeExpiresInSeconds(result ? result.expiresInSeconds : 0);
            setConfirmationCode("");
            setCodeRequested(true);
            notify.showSuccess("Код подтверждения отправлен на ваш номер");
        } catch (e) {
            notify.showError("Не удалось отправить код подтверждения", e);
        } finally {
            setSubmitting(false);
        }
    }, [isPasswordValid, notify]);

    const cancelCodeRequest = useCallback(() => {
        setConfirmationCode("");
        setCodeRequested(false);
        setCodeExpiresInSeconds(0);
    }, []);

    const submitPassword = useCallback(async () => {
        if (!isPasswordValid || !isConfirmationCodeValid) return;

        setSubmitting(true);
        try {
            const updated = await setAccountPassword({ password, confirmationCode });
            if (updated) setAccount(updated);

            resetForm();
            notify.showSuccess("Пароль изменён");
        } catch (e) {
            notify.showError("Не удалось изменить пароль", e);
        } finally {
            setSubmitting(false);
        }
    }, [
        confirmationCode,
        isConfirmationCodeValid,
        isPasswordValid,
        notify,
        password,
        resetForm,
        setAccount,
    ]);

    // Редирект на страницу входа делает SecurityLayout — он смотрит на сессию.
    const logout = useCallback(() => {
        signOut();
    }, [signOut]);

    return {
        account,
        loading,

        password,
        setPassword,
        confirmPassword,
        setConfirmPassword,
        confirmationCode,
        setConfirmationCode,

        passwordError,
        confirmPasswordError,
        isPasswordValid,
        isConfirmationCodeValid,

        codeRequested,
        codeExpiresInSeconds,
        submitting,

        requestCode,
        cancelCodeRequest,
        submitPassword,

        logout,
        notify,
    };
};
